import React from "react";
import { Box, Typography } from "@mui/material";

/**
 * Lightweight viewer for Draft.js JSON content and plain text.
 * Replaces the heavy MUIRichTextEditor readonly mode with compact Typography.
 */

/* ── inline-style renderer ─────────────────────────────── */
const renderStyledText = (block) => {
  const { text, inlineStyleRanges = [] } = block;
  if (!text) return null;
  if (inlineStyleRanges.length === 0) return text;

  // Build per-character style sets
  const charStyles = Array.from({ length: text.length }, () => new Set());
  inlineStyleRanges.forEach(({ offset, length, style }) => {
    for (let i = offset; i < offset + length && i < text.length; i++) {
      charStyles[i].add(style);
    }
  });

  // Group consecutive characters with identical styles
  const segments = [];
  let segStart = 0;
  for (let i = 1; i <= text.length; i++) {
    const prev = [...charStyles[i - 1]].sort().join(",");
    const cur = i < text.length ? [...charStyles[i]].sort().join(",") : null;
    if (prev !== cur) {
      segments.push({ text: text.slice(segStart, i), styles: charStyles[segStart] });
      segStart = i;
    }
  }

  return segments.map((seg, idx) => {
    const sx = {};
    if (seg.styles.has("BOLD")) sx.fontWeight = 600;
    if (seg.styles.has("ITALIC")) sx.fontStyle = "italic";
    if (seg.styles.has("UNDERLINE")) sx.textDecoration = "underline";
    if (seg.styles.has("STRIKETHROUGH"))
      sx.textDecoration = (sx.textDecoration || "") + " line-through";
    if (seg.styles.has("CODE"))
      return (
        <code
          key={idx}
          style={{
            background: "#f5f5f5",
            borderRadius: 3,
            padding: "1px 4px",
            fontSize: "0.85em",
            fontFamily: "monospace",
          }}
        >
          {seg.text}
        </code>
      );
    if (Object.keys(sx).length === 0) return seg.text;
    return (
      <span key={idx} style={sx}>
        {seg.text}
      </span>
    );
  });
};

/* ── block renderer ────────────────────────────────────── */
const renderBlock = (block, idx) => {
  const { text, type } = block;
  // Skip truly empty blocks — use a small spacer
  if (!text?.trim() && type === "unstyled") return <Box key={idx} sx={{ height: 4 }} />;

  const content = renderStyledText(block);

  switch (type) {
    case "header-one":
      return (
        <Typography key={idx} variant="subtitle1" fontWeight={700} sx={{ mt: 0.5 }}>
          {content}
        </Typography>
      );
    case "header-two":
      return (
        <Typography key={idx} variant="subtitle2" fontWeight={600} sx={{ mt: 0.5 }}>
          {content}
        </Typography>
      );
    case "header-three":
      return (
        <Typography key={idx} variant="body1" fontWeight={600} sx={{ mt: 0.25 }}>
          {content}
        </Typography>
      );
    case "blockquote":
      return (
        <Box
          key={idx}
          sx={{
            borderLeft: "3px solid #bdbdbd",
            pl: 1.5,
            my: 0.5,
          }}
        >
          <Typography variant="body2" color="text.secondary" fontStyle="italic">
            {content}
          </Typography>
        </Box>
      );
    case "code-block":
      return (
        <Box
          key={idx}
          component="pre"
          sx={{
            bgcolor: "#f5f5f5",
            borderRadius: 1,
            p: 1,
            my: 0.5,
            fontFamily: "monospace",
            fontSize: "0.82rem",
            overflowX: "auto",
          }}
        >
          {text}
        </Box>
      );
    case "unordered-list-item":
      return (
        <Typography key={idx} variant="body2" color="text.secondary" sx={{ pl: 2, position: "relative", "&::before": { content: '"•"', position: "absolute", left: 4 } }}>
          {content}
        </Typography>
      );
    case "ordered-list-item":
      return (
        <Typography key={idx} variant="body2" color="text.secondary" sx={{ pl: 2 }}>
          {content}
        </Typography>
      );
    default:
      return (
        <Typography key={idx} variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
          {content}
        </Typography>
      );
  }
};

/* ── main component ────────────────────────────────────── */
export default function RichContentViewer({ content }) {
  if (!content) return null;

  // Try to parse as Draft.js JSON
  try {
    const parsed = JSON.parse(content);
    if (parsed && Array.isArray(parsed.blocks)) {
      // Filter out trailing empty blocks to save space
      const blocks = parsed.blocks;
      let lastNonEmpty = blocks.length - 1;
      while (lastNonEmpty >= 0 && !blocks[lastNonEmpty].text?.trim()) lastNonEmpty--;
      const trimmed = blocks.slice(0, lastNonEmpty + 1);

      if (trimmed.length === 0) return null;

      return <Box sx={{ py: 0.25 }}>{trimmed.map(renderBlock)}</Box>;
    }
  } catch {
    // Not JSON — fall through to plain text
  }

  // Plain text fallback
  return (
    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-wrap", py: 0.25, lineHeight: 1.6 }}>
      {content}
    </Typography>
  );
}
